<script setup lang="ts">
import { ref } from "vue";
import { storeToRefs } from "pinia";
import {
  projectWorldBbox,
  useViewSyncedCompute,
  type ScreenRect,
} from "@/composable/usePlanFrameProjection";
import { Facade } from "@/drawing";
import { mergeSideCss, useBranchMergeState } from "@/stores/BranchMergeState";

/**
 * Surcouche HTML du conflit de fusion courant, posée DANS le conteneur du
 * canvas (même géométrie, coordonnées écran du viewer telles quelles) : cadre
 * autour des versions de l'entité, projecteur (voile ailleurs) et légende des
 * couleurs. Les versions elles-mêmes sont des fantômes du moteur (cf.
 * drawing/merge/preview.ts). Rien n'est écrit dans le .dwg.
 */

/** Marge du cadre autour des versions, en pixels écran (de part et d'autre). */
const FRAME_PADDING_PX = 16;

const merge = useBranchMergeState();
const { currentBbox, currentConflict, target, merged } = storeToRefs(merge);
const frame = ref<ScreenRect | null>(null);

useViewSyncedCompute(() => {
  frame.value =
    Facade.isInitialized && currentBbox.value
      ? projectWorldBbox(currentBbox.value, FRAME_PADDING_PX)
      : null;
}, [() => currentBbox.value]);
</script>

<template>
  <div v-if="frame && currentConflict" class="merge-conflict-overlay">
    <div
      class="conflict-frame"
      :style="{
        left: `${frame.left}px`,
        top: `${frame.top}px`,
        width: `${frame.width}px`,
        height: `${frame.height}px`,
      }"
    >
      <div class="conflict-legend">
        <span v-if="currentConflict.target">
          <i class="dot" :style="{ background: mergeSideCss('target') }" />
          {{ target?.name }}
        </span>
        <span v-if="currentConflict.merged">
          <i class="dot" :style="{ background: mergeSideCss('merged') }" />
          {{ merged?.name }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.merge-conflict-overlay {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.conflict-frame {
  position: absolute;
  border: 2px dashed #ffc000;
  border-radius: 6px;
  // L'ombre démesurée fait le voile (cf. AnalysisFramesOverlay) : le trou,
  // c'est le cadre du conflit.
  box-shadow: 0 0 0 100000px rgba(8, 6, 18, 0.42);
}

.conflict-legend {
  position: absolute;
  bottom: 100%;
  left: -2px;
  display: flex;
  gap: 10px;
  margin-bottom: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(20, 20, 26, 0.85);
  color: #fff;
  font-size: 0.72rem;
  white-space: nowrap;
}

.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 4px;
  border-radius: 50%;
}
</style>
